// @flow

import _ from 'lodash/fp';
import React, {Component} from 'react';
import {connect} from 'react-redux';
import {Redirect} from 'react-router-dom';
import {touch, isValid, getFormValues, reset as resetForm} from 'redux-form';
import {
  reset,
  clear,
  createConfigTemplate,
  createProductSlot,
  createRoom,
  fetchData,
  selectProductSlot,
  selectRoom,
  updateConfigTemplate,
  updateProductSlot,
  updateRoom,
  openDialog,
  closeDialog,
  navigateToVariant,
  popDialogNavigation,
  updateRelatedVariant,
  toggleVariant,
  setStandardProduct,
  toggleProduct,
  checkAllVariants,
  clearStandardProduct,
  checkProduct,
  updateVariants,
  updateRooms,
  updateProductSlots,
  updateVariantsForType,
} from '../../actions/configTemplateView';
import {setDirty} from '../../actions/form';
import SaveMenu from '../../components/SaveMenu/SaveMenu';
import DisableContent from '../../components/DisableContent/DisableContent';
import ConfigTemplateForm from '../../components/ConfigTemplateForm/ConfigTemplateForm';
import ServerError from '../../components/ServerError/ServerError';
import LoadingIndicator from '../../components/LoadingIndicator/LoadingIndicator';
import validator from '../../helpers/validator';
import Rooms from '../../components/Rooms/Rooms';
import RoomForm from '../../components/RoomForm/ConfigTemplateRoomForm';
import ProductSlotForm from '../../components/ProductSlotForm/ConfigTemplateProductSlotForm';
import ActionConfirmation from '../ActionConfirmation/ActionConfirmation';
import Navigation from '../../components/Navigation/Navigation';
import './ConfigTemplateContainer.css';
import type {
  ModifiedState,
  NavigationState,
  State_ as ConfigTemplateViewState,
} from '../../reducers/configTemplateView';
import type {
  ConfigTemplateDto,
  ErrorDto,
  LimitedProjectDto,
  ProductDto,
  ProductSlotDto,
  ProductTypeDto,
  RoomDto,
  RoomTemplateDto,
  RoomTypeDto,
} from '../../dtos';

const configTemplateValidation = {
  name: {rule: 'string|required', label: 'Namn'},
};

const basedOnConfigValidation = {
  basedOnConfigId: {rule: 'numeric|required', label: 'Lägenhetskonfiguration'},
};

const basedOnTemplateValidation = {
  basedOnTemplateId: {rule: 'numeric|required', label: 'Konfigurationsmall'},
};

const getBasedOnValidation = values =>
  values && values.rooms === 'CONFIG'
    ? basedOnConfigValidation
    : values && values.rooms === 'TEMPLATE'
      ? basedOnTemplateValidation
      : {};

const getConfigTemplateValidation = values => ({
  ...configTemplateValidation,
  ...getBasedOnValidation(values),
});

const roomValidation = {
  name: {rule: 'string|required', label: 'Namn'},
};

const basicRoomValidation = {
  roomTypeId: {rule: 'numeric|required', label: 'Rumstyp'},
};

const basedOnRoomValidation = {
  basedOnRoomId: {rule: 'numeric|required', label: 'Rum'},
};

const basedOnRoomTemplateValidation = {
  basedOnTemplateId: {rule: 'numeric|required', label: 'Rumsmall'},
};

const getRoomBasedOnValidation = values =>
  values && values.slots === 'ROOM'
    ? basedOnRoomValidation
    : values && values.slots === 'TEMPLATE'
      ? basedOnRoomTemplateValidation
      : basicRoomValidation;

const getRoomValidation = values => ({
  ...roomValidation,
  ...getRoomBasedOnValidation(values),
});

const productSlotValidation = {
  name: {rule: 'string|required', label: 'Namn'},
  productTypeId: {rule: 'numeric|required', label: 'Produkttyp'},
  unit: {rule: 'string', label: 'Enhet'},
  quantity: {rule: 'numeric|min:0', label: 'Antal'},
};

export type Props = {
  setDirty: typeof setDirty,
  reset: typeof reset,
  resetForm: typeof resetForm,
  isConfigTemplateDirty: boolean,
  isProductSlotDirty: boolean,
  isRoomDirty: boolean,
  clear: typeof clear,
  configTemplate: ?ConfigTemplateDto,
  configTemplateFormIsValid: boolean,
  configTemplateFormValues: any,
  createConfigTemplate: typeof createConfigTemplate,
  createProductSlot: typeof createProductSlot,
  createRoom: typeof createRoom,
  error: ?ErrorDto,
  fetchData: typeof fetchData,
  isFetching: boolean,
  params: any,
  prevPath: string,
  products: ?ProductDto[],
  productSlot: ?ProductSlotDto,
  productSlotError: ?ErrorDto,
  productSlotFormIsValid: boolean,
  productSlotFormValues: any,
  productSlotIsFetching: boolean,
  productSlotRoomId: ?number,
  productTypes: ?ProductTypeDto[],
  room: ?RoomDto,
  roomError: ?ErrorDto,
  roomFormIsValid: boolean,
  roomFormValues: any,
  roomIsFetching: boolean,
  rooms: RoomDto[],
  roomTypes: ?RoomTypeDto[],
  selectProductSlot: typeof selectProductSlot,
  selectRoom: typeof selectRoom,
  touch: typeof touch,
  updateConfigTemplate: typeof updateConfigTemplate,
  updateProductSlot: typeof updateProductSlot,
  updateRoom: typeof updateRoom,
  openDialog: typeof openDialog,
  closeDialog: typeof closeDialog,
  dialogError: ?ErrorDto,
  dialogIsFetching: boolean,
  showDialog: boolean,
  navigateToVariant: typeof navigateToVariant,
  popDialogNavigation: typeof popDialogNavigation,
  dialogNavigation: ?NavigationState,
  updateRelatedVariant: typeof updateRelatedVariant,
  toggleVariant: typeof toggleVariant,
  setStandardProduct: typeof setStandardProduct,
  modifiedData: ModifiedState,
  standardProductId: ?number,
  toggleProduct: typeof toggleProduct,
  sharedChecked: ?boolean,
  checkAllVariants: typeof checkAllVariants,
  clearStandardProduct: typeof clearStandardProduct,
  checkProduct: typeof checkProduct,
  updateVariants: typeof updateVariants,
  updateRooms: typeof updateRooms,
  updateProductSlots: typeof updateProductSlots,
  updateVariantsForType: typeof updateVariantsForType,
  configTemplates: any[],
  projects: LimitedProjectDto[],
  roomTemplates: RoomTemplateDto[],
  configTemplateList: ConfigTemplateDto[],
};

class ConfigTemplateContainer extends Component<Props> {
  props: Props;

  componentWillMount() {
    this.props.fetchData(this.props.params.id);
  }

  componentWillUnmount() {
    this.props.clear();
  }

  setDirty(form: 'configTemplate' | 'configTemplateRoom' | 'configTemplateProductSlot') {
    const {
      setDirty,
      isConfigTemplateDirty,
      isRoomDirty,
      isProductSlotDirty,
    } = this.props;

    if (form === 'configTemplate') {
      if (isConfigTemplateDirty) {
        return;
      }
    } else if (form === 'configTemplateRoom') {
      if (isRoomDirty) {
        return;
      }
    } else if (form === 'configTemplateProductSlot') {
      if (isProductSlotDirty) {
        return;
      }
    } else {
      return;
    }
    setDirty(true, form);
  }

  reset() {
    const {
      setDirty,
      reset,
      resetForm,
    } = this.props;
    reset();
    resetForm('configTemplate');
    resetForm('configTemplateRoom');
    resetForm('configTemplateProductSlot');
    setDirty(false, 'configTemplate');
    setDirty(false, 'configTemplateRoom');
    setDirty(false, 'configTemplateProductSlot');
  }

  validateRoomForm() {
    const {
      roomFormIsValid,
      roomFormValues,
      touch,
    } = this.props;

    const roomFields = _.keys(getRoomValidation(roomFormValues));
    touch('configTemplateRoom', ...roomFields);

    return roomFormIsValid;
  }

  validateProductSlotForm() {
    const {
      productSlotFormIsValid,
      touch,
    } = this.props;

    const productSlotFields = _.keys(productSlotValidation);
    touch('configTemplateProductSlot', ...productSlotFields);
    return productSlotFormIsValid;
  }

  save() {
    const {
      configTemplate,
      configTemplateFormIsValid,
      configTemplateFormValues,
      createConfigTemplate,
      createProductSlot,
      createRoom,
      productSlot,
      productSlotFormValues,
      productSlotRoomId,
      room,
      roomFormValues,
      touch,
      updateConfigTemplate,
      updateProductSlot,
      updateRoom,
      modifiedData,
      standardProductId,
      closeDialog,
    } = this.props;
    const configTemplateFields = _.keys(
      !configTemplate
        ? getConfigTemplateValidation(configTemplateFormValues)
        : configTemplateValidation
    );
    touch('configTemplate', ...configTemplateFields);
    if (!configTemplateFormIsValid) {
      return;
    }

    closeDialog();

    if (room) {
      if (!this.validateRoomForm()) {
        return;
      }

      if (room.id) {
        updateRoom(room.id, roomFormValues);
      } else if (!!configTemplate) {
        createRoom(configTemplate.id, roomFormValues);
      }
    }

    if (productSlot) {
      if (!this.validateProductSlotForm()) {
        return;
      }

      if (productSlot.id) {
        updateProductSlot(productSlot.id, {
          ...productSlotFormValues,
          standardProductId,
          config: _.flow(
            _.values,
            _.filter('checked'),
            _.map(c => ({
              ...c,
              standardProducts: _.flow(
                _.toPairs,
                _.map(([productTypeId, productId]) => ({
                  productId,
                  productTypeId: _.toNumber(productTypeId),
                })),
                _.filter(x => !!x.productId),
              )(c.standardProducts),
            })),
          )(modifiedData),
        });
      } else if (!!productSlotRoomId) {
        createProductSlot(productSlotRoomId, productSlotFormValues);
      }
    }

    if (!configTemplate) {
      createConfigTemplate(_.pick(configTemplateFields, configTemplateFormValues));
    } else {
      updateConfigTemplate(configTemplate.id, configTemplateFormValues);
    }
  }

  render() {
    const {
      isConfigTemplateDirty,
      isRoomDirty,
      isProductSlotDirty,
      configTemplate,
      error,
      isFetching,
      params: {id},
      prevPath,
      products,
      productSlot,
      productSlotError,
      productSlotFormValues,
      productSlotIsFetching,
      productSlotRoomId,
      productTypes,
      room,
      roomError,
      roomIsFetching,
      rooms,
      roomTypes,
      selectProductSlot,
      selectRoom,
      updateConfigTemplate,
      updateProductSlot,
      updateRoom,
      openDialog,
      closeDialog,
      dialogError,
      dialogIsFetching,
      showDialog,
      navigateToVariant,
      popDialogNavigation,
      dialogNavigation,
      updateRelatedVariant,
      toggleVariant,
      setStandardProduct,
      modifiedData,
      toggleProduct,
      sharedChecked,
      checkAllVariants,
      clearStandardProduct,
      checkProduct,
      updateVariants,
      updateRooms,
      updateProductSlots,
      updateVariantsForType,
      configTemplateFormValues,
      configTemplates,
      projects,
      roomTemplates,
      roomFormValues,
      configTemplateList,
      clear,
      fetchData,
    } = this.props;

    return (
      <div className="ConfigTemplateContainer relative">
        <div className="row align-justify align-middle content-header">
          <div className="column shrink">
            <div className="row no-row-margins align-middle">
              <Navigation
                current={configTemplate}
                list={configTemplateList}
                prevPath={prevPath}
                clear={clear}
                fetchData={fetchData} />
              <div className="content-title">
                {(configTemplate && configTemplate.name) || 'Ny konfiguration'}
              </div>
            </div>
          </div>
          <div className="column shrink horizontal-button-list">
            {configTemplate &&
              <DisableContent disabled={isConfigTemplateDirty}>
                <ActionConfirmation
                  id="archiveConfigTemplate"
                  className="secondary button"
                  message={`Vill du verkligen ${configTemplate.archived ? 'av' : ''}arkivera den här konfigurationen?`}
                  buttonText={configTemplate.archived ? 'Avarkivera' : 'Arkivera'}
                  action={() => updateConfigTemplate(configTemplate.id, {
                    archived: !configTemplate.archived,
                  })} />
              </DisableContent>
            }
          </div>
        </div>
        <LoadingIndicator isLoading={isFetching || roomIsFetching || productSlotIsFetching} />
        <ServerError error={error} />
        <div className="content-section box">
          <ConfigTemplateForm
            initialValues={configTemplate || {rooms: 'NONE'}}
            configTemplate={configTemplate}
            configTemplates={configTemplates}
            projects={projects}
            formValues={configTemplateFormValues}
            onUserChange={() => this.setDirty('configTemplate')}
            validate={values => validator(getConfigTemplateValidation(values), values)} />
        </div>
        {configTemplate && configTemplate.id &&
          <div className="content-section rooms-section">
            <div className="row no-row-margins">
              <div className="box-side-tabs shrink">
                <DisableContent disabled={isRoomDirty || isProductSlotDirty}>
                  <Rooms
                    onSelectRoom={r => selectRoom(r)}
                    onSelectProductSlot={(roomId, p) => selectProductSlot(roomId, p)}
                    onAddRoom={() => selectRoom(null)}
                    onAddProductSlot={roomId => selectProductSlot(roomId, null)}
                    selectedRoom={room}
                    selectedProductSlot={productSlot}
                    selectedProductSlotRoomId={productSlotRoomId}
                    rooms={_.filter({archived: null}, rooms)} />
                </DisableContent>
              </div>
              <div className="box expand">
                {room && !productSlot && (() => {
                  const roomIndex = _.findIndex({id: room.id}, rooms);
                  return (
                    <div>
                      <ServerError error={roomError} />
                      <RoomForm
                        roomTemplates={roomTemplates}
                        projects={projects}
                        formValues={roomFormValues}
                        initialValues={!!room.id ? room : {slots: 'NONE'}}
                        room={room}
                        onArchive={() => updateRoom(room.id, {archived: !room.archived})}
                        roomTypes={roomTypes}
                        onUserChange={() => this.setDirty('configTemplateRoom')}
                        validate={values => validator(getRoomValidation(values), values)}
                        isDirty={isRoomDirty}
                        updateRooms={(sortDirection: 'up' | 'down') => updateRooms(configTemplate.id, room.id, sortDirection)}
                        sortDownDisabled={roomIndex === rooms.length - 1}
                        sortUpDisabled={roomIndex <= 0} />
                    </div>
                  );
                })()}
                {productSlot && !room && (() => {
                  const slotRoom = _.find({id: productSlotRoomId}, rooms);
                  const allSlots = !!slotRoom ? slotRoom.productSlots : [];
                  const slotIndex = _.findIndex({id: productSlot.id}, allSlots);
                  return (
                    <div>
                      <ServerError error={productSlotError} />
                      <ProductSlotForm
                        initialValues={productSlot}
                        formValues={productSlotFormValues}
                        productSlot={productSlot}
                        productTypes={productTypes}
                        room={_.find({id: productSlotRoomId}, rooms)}
                        products={products}
                        onArchive={() => updateProductSlot(productSlot.id, {archived: !productSlot.archived})}
                        onUserChange={() => this.setDirty('configTemplateProductSlot')}
                        validate={values => validator(productSlotValidation, values)}
                        openDialog={openDialog}
                        closeDialog={closeDialog}
                        dialogError={dialogError}
                        dialogIsFetching={dialogIsFetching}
                        dialogNavigation={dialogNavigation}
                        showDialog={showDialog}
                        navigateToVariant={navigateToVariant}
                        popDialogNavigation={popDialogNavigation}
                        updateRelatedVariant={updateRelatedVariant}
                        toggleVariant={toggleVariant}
                        setStandardProduct={setStandardProduct}
                        modifiedData={modifiedData}
                        toggleProduct={toggleProduct}
                        sharedChecked={sharedChecked}
                        checkAllVariants={checkAllVariants}
                        clearStandardProduct={clearStandardProduct}
                        isDirty={isProductSlotDirty}
                        checkProduct={checkProduct}
                        updateVariants={updateVariants}
                        updateVariantsForType={updateVariantsForType}
                        updateProductSlots={(sortDirection: 'up' | 'down') => {
                          if (!!slotRoom) {
                            updateProductSlots(slotRoom.id, productSlot.id, sortDirection);
                          }
                        }}
                        sortDownDisabled={isProductSlotDirty || slotIndex === allSlots.length - 1}
                        sortUpDisabled={isProductSlotDirty || slotIndex <= 0} />
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        }
        {configTemplate && configTemplate.id !== _.toNumber(id) &&
          <Redirect to={`${prevPath}/${configTemplate.id}`} />
        }
        {(isConfigTemplateDirty || isRoomDirty || isProductSlotDirty) &&
          <SaveMenu
            isLoading={isFetching}
            onDiscard={() => this.reset()}
            onSave={() => this.save()} />
        }
      </div>
    );
  }
}

const mapStateToProps = ({
  configTemplateView: {
    configTemplate: {
      active: configTemplate,
      error,
      isDirty: isConfigTemplateDirty,
      isFetching,
    },
    rooms,
    room: {
      active: room,
      error: roomError,
      isDirty: isRoomDirty,
      isFetching: roomIsFetching,
    },
    productSlot: {
      active: productSlot,
      roomId: productSlotRoomId,
      error: productSlotError,
      isDirty: isProductSlotDirty,
      isFetching: productSlotIsFetching,
      dialog: {
        error: dialogError,
        isFetching: dialogIsFetching,
        show: showDialog,
        navigation: dialogNavigation,
        modifiedData,
        standardProductId,
        sharedChecked,
      },
    },
    roomTypes,
    productTypes,
    products,
    configTemplates,
    projects,
    roomTemplates,
  },
  configTemplateList: {
    filteredList: configTemplateList,
  },
  form,
}: {
  configTemplateView: ConfigTemplateViewState,
  configTemplateList: any,
  form: any,
}) => ({
  configTemplate,
  rooms,
  room,
  roomError,
  roomIsFetching,
  productSlot,
  productSlotRoomId,
  productSlotError,
  productSlotIsFetching,
  error,
  isConfigTemplateDirty,
  isRoomDirty,
  isProductSlotDirty,
  isFetching,
  roomTypes,
  productTypes,
  products,
  configTemplateFormIsValid: isValid('configTemplate')({form}),
  configTemplateFormValues: getFormValues('configTemplate')({form}),
  roomFormIsValid: isValid('configTemplateRoom')({form}),
  roomFormValues: getFormValues('configTemplateRoom')({form}),
  productSlotFormIsValid: isValid('configTemplateProductSlot')({form}),
  productSlotFormValues: getFormValues('configTemplateProductSlot')({form}),
  dialogError,
  dialogIsFetching,
  showDialog,
  dialogNavigation,
  modifiedData,
  standardProductId,
  sharedChecked,
  configTemplates,
  projects,
  roomTemplates,
  configTemplateList,
});

const mapDispatchToProps = dispatch => ({
  setDirty: (dirty, form) =>
    dispatch(setDirty(dirty, form)),
  resetForm: form =>
    dispatch(resetForm(form)),
  reset: () =>
    dispatch(reset()),
  fetchData: id =>
    dispatch(fetchData(id)),
  updateConfigTemplate: (id, data) =>
    dispatch(updateConfigTemplate(id, data)),
  createConfigTemplate: data =>
    dispatch(createConfigTemplate(data)),
  updateRoom: (id, data) =>
    dispatch(updateRoom(id, data)),
  createRoom: (configId, data) =>
    dispatch(createRoom(configId, data)),
  updateProductSlot: (id, data) =>
    dispatch(updateProductSlot(id, data)),
  createProductSlot: (roomId, data) =>
    dispatch(createProductSlot(roomId, data)),
  selectRoom: room =>
    dispatch(selectRoom(room)),
  selectProductSlot: (roomId, productSlot) =>
    dispatch(selectProductSlot(roomId, productSlot)),
  touch: (form, ...fields) =>
    dispatch(touch(form, ...fields)),
  clear: () =>
    dispatch(clear()),
  openDialog: (productTypeId, rootName) =>
    dispatch(openDialog(productTypeId, rootName)),
  closeDialog: () =>
    dispatch(closeDialog()),
  navigateToVariant: variant =>
    dispatch(navigateToVariant(variant)),
  popDialogNavigation: depth =>
    dispatch(popDialogNavigation(depth)),
  updateRelatedVariant: (variant, data) =>
    dispatch(updateRelatedVariant(variant, data)),
  toggleVariant: (variant, expand) =>
    dispatch(toggleVariant(variant, expand)),
  setStandardProduct: (product, productType) =>
    dispatch(setStandardProduct(product, productType)),
  toggleProduct: product =>
    dispatch(toggleProduct(product)),
  checkAllVariants: checked =>
    dispatch(checkAllVariants(checked)),
  clearStandardProduct: productType =>
    dispatch(clearStandardProduct(productType)),
  checkProduct: (productId, checked) =>
    dispatch(checkProduct(productId, checked)),
  updateVariants: (productId, data) =>
    dispatch(updateVariants(productId, data)),
  updateRooms: (configId, roomId, sortDirection: 'up' | 'down') =>
    dispatch(updateRooms(configId, roomId, sortDirection)),
  updateProductSlots: (roomId, productSlotId, sortDirection: 'up' | 'down') =>
    dispatch(updateProductSlots(roomId, productSlotId, sortDirection)),
  updateVariantsForType: (productTypeId, data) =>
    dispatch(updateVariantsForType(productTypeId, data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ConfigTemplateContainer);



// WEBPACK FOOTER //
// ./src/containers/ConfigTemplateContainer/ConfigTemplateContainer.js